import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Briefcase, UploadSimple, CircleNotch } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail, useCareers, Career } from "../lib/api";

const ApplyDialog = ({ career, onClose }: { career: Career | null; onClose: () => void }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [resume, setResume] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!career) return;
    if (!resume) {
      toast.error("Please attach your resume (PDF, DOC, or DOCX).");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("cover_letter", form.cover_letter);
      data.append("career_id", career.id);
      data.append("resume", resume);
      await api.post("/careers/apply", data);
      toast.success("Application submitted. Our team will be in touch.");
      onClose();
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!career} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-none" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Apply — {career?.title}</DialogTitle>
          <DialogDescription>Attach your resume and tell us why you're a fit.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="apply-name">Full Name *</Label>
              <Input id="apply-name" data-testid="apply-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apply-email">Email *</Label>
              <Input id="apply-email" data-testid="apply-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="apply-phone">Phone</Label>
            <Input id="apply-phone" data-testid="apply-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="apply-resume">Resume (PDF/DOC/DOCX, max 5MB) *</Label>
            <label
              htmlFor="apply-resume"
              className="flex items-center gap-3 border border-dashed border-line px-4 py-3.5 cursor-pointer text-sm text-sub hover:border-[var(--blueprint)] transition-colors"
            >
              <UploadSimple size={18} className="text-blueprint" />
              {resume ? resume.name : "Click to attach your resume"}
            </label>
            <input
              id="apply-resume"
              data-testid="apply-resume-input"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => setResume(e.target.files?.[0] || null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="apply-cover">Cover Note</Label>
            <Textarea id="apply-cover" data-testid="apply-cover-input" rows={4} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="rounded-none" />
          </div>
          <Button type="submit" data-testid="apply-submit-button" disabled={submitting} className="w-full rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <CircleNotch size={16} className="animate-spin" /> Submitting…
              </span>
            ) : (
              "Submit Application"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CareersPage() {
  const { data: careers = [] } = useCareers();
  const [selected, setSelected] = useState<Career | null>(null);

  return (
    <div data-testid="careers-page">
      <PageHero
        overline="Careers"
        title="Build your career on solid ground."
        subtitle="Join a veteran-led team rebuilding Detroit. Veterans, tradespeople, and builders of every background are encouraged to apply."
      />

      <section className="bg-canvas py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="overline-label mb-4">Open Positions ({careers.length})</p>
          <div className="space-y-6">
            {careers.map((c, i) => (
              <article key={c.id} data-testid={`career-card-${i}`} className="border border-line bg-panel p-7 md:p-9">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                  <div>
                    <h3 className="font-heading font-medium text-2xl tracking-tight text-ink">{c.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-4 text-xs font-mono-tech text-sub">
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase size={13} className="text-blueprint" /> {c.type} · {c.department}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} className="text-blueprint" /> {c.location}
                      </span>
                    </div>
                  </div>
                  <Button
                    data-testid={`career-apply-btn-${i}`}
                    onClick={() => setSelected(c)}
                    className="rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black shrink-0"
                  >
                    Apply Now
                  </Button>
                </div>
                <p className="mt-5 text-sm text-sub leading-relaxed max-w-3xl">{c.description}</p>
                <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {(c.requirements || []).map((r) => (
                    <li key={r} className="text-sm text-sub flex gap-2.5">
                      <span className="text-blueprint font-mono-tech">—</span> {r}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
            {careers.length === 0 && (
              <p className="text-sub text-sm border border-line p-8 text-center">
                No open positions right now. Check back soon or send your resume to our team via the contact page.
              </p>
            )}
          </div>
        </div>
      </section>

      <ApplyDialog career={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
